import { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { InputText } from 'primereact/inputtext';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import CommonConfig from '../../utils/constant';
import { useCatchResponse } from '../../utils/useCatchResponse';

const ParentDashboard = () => {
    const { catchResponse } = useCatchResponse();
    const [securityUserID, setSecurityUserID] = useState("");
    const [role, setRole] = useState(null);
    const [studentWiseAttendanceList, setStudentWiseAttendanceList] = useState([]);

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        setSecurityUserID(loginData?._id);
        setRole(loginData?.UserRole);
        getFinancialYearData(loginData);
    }, []);

    const getFinancialYearData = async (userData) => {
        // Student: use their own id + the sidebar-selected academic year (the
        // `selectedAcademicYear` cookie, kept in sync by the sidebar year
        // picker); fall back to the current year by date only if it isn't set.
        if (userData?.UserRole === 'Student') {
            const sid = userData?.CreatedBy;
            let fy = CommonConfig.getCookie('selectedAcademicYear') || '';
            if (!fy) {
                const years = await CommonConfig.getFinancialYears(sid) || [];
                const now = new Date();
                const cur = years.find(y => y?.StartDate && y?.EndDate && new Date(y.StartDate) <= now && now <= new Date(y.EndDate));
                fy = cur?._id || years[0]?._id || '';
            }
            GetParentDashboardData(userData?._id, fy);
            return;
        }
        const selectedYear = CommonConfig.getCookie('selectedAcademicYear');
        const childId = userData?.SelectedChildData?._id;
        if (userData?.UserRole === 'Parent' && childId) {
            GetParentDashboardData(childId, selectedYear);
        }
    }

    const GetParentDashboardData = async (id, financialId) => {
        try {
            let data = {
                StudentId: id,
                FinancialYearId: financialId
            }
            Loader.show();
            await api.post(APIConstant.path.GetParentDashboardData, data).then(async response => {
                let res = response;
                if (res.success) {
                    setStudentWiseAttendanceList(res?.data);
                }
                Loader.hide();
            }).catch(err => {
                Loader.hide();
                console.log(err);
                catchResponse(err);
            });
        } catch (err) {
            console.log(err);
            catchResponse(err);
        }
    }

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'P':
                return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
            case 'A':
                return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
            case 'L':
                return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
            case 'PARTIAL':
                return 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)';
            case 'H':
                return 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)';
            default:
                return 'linear-gradient(135deg, #7c7c83ff 0%, #afaff4ff 100%)';
        }
    };

    const getAttendanceIcon = (status) => {
        switch (status?.toUpperCase()) {
            case 'P': return 'pi-check-circle';
            case 'A': return 'pi-times-circle';
            case 'L': return 'pi-clock';
            case 'PARTIAL': return 'pi-info-circle';
            case 'H': return 'pi-calendar';
            default: return 'pi-check-circle';
        }
    };

    const getStatusName = (status) => {
        switch (status?.toUpperCase()) {
            case 'P': return 'Present';
            case 'A': return 'Absent';
            case 'L': return 'Late';
            case 'PARTIAL': return 'Partial';
            case 'H': return 'Holiday';
            default: return status;
        }
    };

    const AttendanceCard = ({ day, data }) => {
        const statusBg = () => {
            switch (data?.status?.toUpperCase()) {
                case 'P':
                    return '#f0fdf4';
                case 'A':
                    return '#fef2f2';
                case 'L':
                    return '#fffbeb';
                case 'PARTIAL':
                    return '#fff7ed';
                case 'H':
                    return '#f0f4ff';
                default:
                    return '#f5f5f5';
            }
        };

        const statusColor = () => {
            switch (data?.status?.toUpperCase()) {
                case 'P':
                    return '#16a34a';
                case 'A':
                    return '#dc2626';
                case 'L':
                    return '#d97706';
                case 'PARTIAL':
                    return '#ea580c';
                case 'H':
                    return '#7c3aed';
                default:
                    return '#64748b';
            }
        };

        return (
            <div style={{
                background: statusBg(),
                border: `1px solid ${statusColor()}33`,
                borderRadius: '0.5rem',
                padding: '1rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
            }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div>
                        <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#475569', marginBottom: '0.25rem' }}>{day}</div>
                        <div style={{ fontSize: '0.75rem', color: '#78849e' }}>{data?.date}</div>
                    </div>
                    <div style={{
                        background: `${statusColor()}20`,
                        color: statusColor(),
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <i className={`pi ${getAttendanceIcon(data?.status)}`} style={{ fontSize: '0.875rem' }}></i>
                    </div>
                </div>
                {Array.isArray(data?.subjects) && data.subjects.length > 0 ? (
                    <div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: statusColor(), marginBottom: '0.4rem', textAlign: 'center' }}>
                            {data?.summary}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                            {data.subjects.map((s, i) => {
                                const sc = s.status === 'P' ? '#16a34a' : s.status === 'A' ? '#dc2626' : s.status === 'L' ? '#d97706' : '#64748b';
                                return (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '0.375rem', padding: '0.35rem 0.5rem' }}>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontSize: '0.8rem', fontWeight: 500, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.subjectName}</div>
                                            {s.time ? <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{s.time}</div> : null}
                                        </div>
                                        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: sc, background: `${sc}20`, borderRadius: '0.3rem', padding: '0.15rem 0.4rem', whiteSpace: 'nowrap' }}>
                                            {getStatusName(s.status)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <div style={{
                        padding: '0.75rem 0.5rem',
                        background: statusColor(),
                        color: 'white',
                        borderRadius: '0.375rem',
                        textAlign: 'center',
                        fontWeight: '600',
                        fontSize: '0.875rem'
                    }}>
                        {getStatusName(data?.status)}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-home"></i>
                    <h2>Parent Dashboard</h2>
                </div>
                <div className="p-d-flex p-ai-center p-jc-between">
                    <div className="searchbar-area e-searchbar-area">
                        <i className="pi pi-search" />
                        <InputText
                            placeholder="Global Search"
                        />
                    </div>
                </div>
            </div>
            
            <div style={{ paddingBlock: '1.5rem', background: '#fff' }}>
                {/* Attendance Section */}
                <div style={{ marginBottom: '2rem' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '1rem',
                        paddingBottom: '1rem',
                        borderBottom: '1px solid #e5e7eb'
                    }}>
                        <i className="pi pi-calendar" style={{ fontSize: '1.125rem', color: '#1c515e', fontWeight: 'bold' }}></i>
                        <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#1c515e' }}>Attendance Overview</h3>
                        <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                            {[
                                { c: '#16a34a', l: 'Present' },
                                { c: '#dc2626', l: 'Absent' },
                                { c: '#d97706', l: 'Late' },
                                { c: '#7c3aed', l: 'Holiday' },
                            ].map((s) => (
                                <span key={s.l} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', color: '#64748b' }}>
                                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: s.c }} />
                                    {s.l}
                                </span>
                            ))}
                        </div>
                    </div>

                    {studentWiseAttendanceList?.attendance && studentWiseAttendanceList.attendance.length > 0 ? (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                            gap: '1rem'
                        }}>
                            {studentWiseAttendanceList.attendance.map((item, index) => (
                                <AttendanceCard
                                    key={index}
                                    day={item?.day}
                                    data={item}
                                />
                            ))}
                        </div>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            color: '#78849e',
                            padding: '2rem',
                            background: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.5rem'
                        }}>
                            No attendance data available
                        </div>
                    )}
                </div>

                {/* Timetable Section */}
                <div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '1rem',
                        paddingBottom: '1rem',
                        borderBottom: '1px solid #e5e7eb'
                    }}>
                        <i className="pi pi-clock" style={{ fontSize: '1.125rem', color: '#1c515e', fontWeight: 'bold' }}></i>
                        <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#1c515e' }}>Today's Timetable</h3>
                    </div>

                    <div style={{
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        padding: '1rem'
                    }}>
                        {studentWiseAttendanceList?.timetable && studentWiseAttendanceList.timetable.length > 0 ? (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
                                gap: '0.75rem'
                            }}>
                                {studentWiseAttendanceList.timetable.map((item, index) => {
                                    const isBreak = item?.subject === 'Break';
                                    const isFree = !item?.subject || item?.subject === '-';
                                    const accent = isBreak ? '#f59e0b' : (isFree ? '#94a3b8' : '#1c515e');
                                    return (
                                        <div key={index} style={{
                                            background: '#fff',
                                            border: '1px solid #e5e7eb',
                                            borderLeft: `4px solid ${accent}`,
                                            borderRadius: '0.5rem',
                                            padding: '0.75rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.35rem'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                                <span style={{
                                                    fontSize: '0.72rem',
                                                    fontWeight: '600',
                                                    color: '#1c515e',
                                                    background: '#e8f0f2',
                                                    borderRadius: '999px',
                                                    padding: '0.15rem 0.6rem',
                                                    whiteSpace: 'nowrap'
                                                }}>{item?.time || '-'}</span>
                                                <i className={isBreak ? 'pi pi-coffee' : 'pi pi-book'} style={{ color: accent, fontSize: '0.9rem', opacity: 0.75 }}></i>
                                            </div>
                                            <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#1c515e', lineHeight: 1.25 }}>
                                                {isFree ? 'Free Period' : item?.subject}
                                            </div>
                                            {item?.teacher && item?.teacher !== '-' && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: '#64748b' }}>
                                                    <i className="pi pi-user" style={{ fontSize: '0.7rem' }}></i>
                                                    <span>{item?.teacher}</span>
                                                </div>
                                            )}
                                            {Array.isArray(item?.parallel) && item.parallel.map((ps, pi) => (
                                                <div key={pi} style={{ marginTop: '0.2rem', paddingTop: '0.2rem', borderTop: '1px dashed #e2e8f0' }}>
                                                    <div style={{ fontWeight: '600', fontSize: '0.85rem', color: '#1c515e' }}>{ps?.subject}</div>
                                                    {ps?.teacher && ps?.teacher !== '-' && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: '#64748b' }}>
                                                            <i className="pi pi-user" style={{ fontSize: '0.7rem' }}></i>
                                                            <span>{ps?.teacher}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', color: '#78849e', padding: '2rem' }}>
                                No timetable available
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ParentDashboard;